import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Button } from "@shop-ledger/ui-components";
import { usePlatformClient } from "../../api/ClientContext";
import { useImpersonation } from "../../auth/ImpersonationContext";
import { ConfirmDialog, useConfirmDialog } from "../../components/ConfirmDialog";
import { MockBadge } from "../../components/MockBadge";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";
import { DASHBOARD_URL, PLATFORM_API_MODE } from "../../env";
import type { DemoResetResult, ImpersonationSession, PlatformTenantDetail } from "../../api/types";

function bytesToGb(bytes: number): string {
  return (bytes / 1024 ** 3).toFixed(1);
}

/**
 * Business detail page: usage (view-only), suspend/reactivate, on-demand backup/export
 * trigger, the onboarding checklist, and "view as business" impersonation. Every
 * mutating action here goes through `ConfirmDialog`.
 *
 * Real vs. mocked, per field (see this app's ARCHITECTURE.md's shape-difference table):
 *  - Suspend/reactivate/backup-trigger/impersonate are genuinely live HTTP calls against
 *    `realPlatformApiClient` by default.
 *  - `plan_code` and `usage.storage_used_bytes`/`storage_quota_bytes` are `null` when the
 *    real client is active — no platform response schema surfaces them yet (verified
 *    directly, not assumed) — rendered with a `MockBadge` "not available" rather than
 *    fabricated.
 *  - The onboarding checklist below is the REAL `GET /platform/tenants/{id}/checklist`
 *    (`getTenantChecklist`), computed live from actual branch/product/sale counts in BOTH
 *    client modes — genuinely real, no `MockBadge` needed on real mode; read-only (nothing
 *    to "mark complete" — it reflects actual data, not an operator's click).
 */
export function TenantDetailPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const client = usePlatformClient();
  const impersonation = useImpersonation();
  const isRealMode = PLATFORM_API_MODE !== "mock";
  const { data: tenant, loading, error, reload } = useAsyncData(
    () => (tenantId ? client.getTenant(tenantId) : Promise.reject(new Error("Missing tenant id"))),
    [client, tenantId],
  );
  const {
    data: checklist,
    loading: checklistLoading,
    error: checklistError,
  } = useAsyncData(
    () => (tenantId ? client.getTenantChecklist(tenantId) : Promise.reject(new Error("Missing tenant id"))),
    [client, tenantId],
  );

  const suspendDialog = useConfirmDialog<PlatformTenantDetail>();
  const reactivateDialog = useConfirmDialog<PlatformTenantDetail>();
  const backupDialog = useConfirmDialog<{ backup_id: string; status: string }>();
  const impersonateDialog = useConfirmDialog<ImpersonationSession>();
  const resetDemoDialog = useConfirmDialog<DemoResetResult>();
  const [suspendReason, setSuspendReason] = useState("");
  const [impersonateReason, setImpersonateReason] = useState("");
  const [backupResult, setBackupResult] = useState<string | null>(null);
  const [resetDemoResult, setResetDemoResult] = useState<string | null>(null);

  if (error) {
    return (
      <div className="page">
        <StatusBanner kind="error">{error}</StatusBanner>
      </div>
    );
  }

  if (loading || !tenant) {
    return (
      <div className="page">
        <p className="muted">Loading tenant…</p>
      </div>
    );
  }

  const hasStorageData = tenant.usage.storage_used_bytes !== null && tenant.usage.storage_quota_bytes !== null;
  const quotaPercent = hasStorageData
    ? (tenant.usage.storage_used_bytes! / tenant.usage.storage_quota_bytes!) * 100
    : null;

  return (
    <div className="page">
      <Link to="/tenants" className="sl-back-link">
        ← Back to Businesses
      </Link>
      <div className="page__header">
        <div>
          <h1 className="page__title">{tenant.business_name}</h1>
          <p className="page__subtitle">
            {tenant.business_type ?? "Unclassified"} · {tenant.currency} · Plan{" "}
            {tenant.plan_code ?? "—"}{" "}
            <MockBadge
              live={false}
              label={tenant.plan_code ? "mock" : "not available"}
              title="Plan/quota details aren't available yet for this deployment."
            />{" "}
            ·{" "}
            <span className={`badge ${tenant.is_suspended ? "badge--suspended" : "badge--active"}`}>
              {tenant.is_suspended ? "Suspended" : "Active"}
            </span>
            {tenant.is_demo ? (
              <>
                {" "}
                <span className="badge badge--demo">DEMO</span>
              </>
            ) : null}
          </p>
        </div>
        <div className="form-actions">
          {tenant.is_suspended ? (
            <Button type="button" variant="primary" size="md" onClick={() => reactivateDialog.setOpen(true)}>
              Reactivate business
            </Button>
          ) : (
            <Button type="button" variant="danger" size="md" onClick={() => suspendDialog.setOpen(true)}>
              Suspend business
            </Button>
          )}
          <Button type="button" variant="secondary" size="md" onClick={() => backupDialog.setOpen(true)}>
            Trigger backup / export
          </Button>
          <Button type="button" variant="secondary" size="md" onClick={() => impersonateDialog.setOpen(true)}>
            View as business
          </Button>
          {tenant.is_demo ? (
            <Button type="button" variant="danger" size="md" onClick={() => resetDemoDialog.setOpen(true)}>
              Reset demo data
            </Button>
          ) : null}
          <Link to={`/audit-log?tenant=${tenant.id}`} className="sl-button sl-button--ghost sl-button--md">
            View audit log
          </Link>
        </div>
      </div>

      {backupResult ? <StatusBanner kind="info">{backupResult}</StatusBanner> : null}
      {resetDemoResult ? <StatusBanner kind="info">{resetDemoResult}</StatusBanner> : null}

      <div className="panel">
        <h2 className="panel__title">Usage (view-only)</h2>
        <div className="stat-row">
          <div className="stat-tile">
            <p className="stat-tile__label">Branches</p>
            <p className="stat-tile__value">{tenant.usage.branch_count}</p>
          </div>
          <div className="stat-tile">
            <p className="stat-tile__label">Active users</p>
            <p className="stat-tile__value">{tenant.usage.active_users}</p>
          </div>
          <div className="stat-tile">
            <p className="stat-tile__label">
              Storage used{" "}
              <MockBadge
                live={false}
                label={hasStorageData ? "mock" : "not available"}
                title="Storage usage isn't tracked yet for this deployment."
              />
            </p>
            {hasStorageData ? (
              <p className={`stat-tile__value ${quotaPercent! >= 80 ? "stat-tile__value--warning" : ""}`}>
                {bytesToGb(tenant.usage.storage_used_bytes!)} / {bytesToGb(tenant.usage.storage_quota_bytes!)} GB
              </p>
            ) : (
              <p className="stat-tile__value muted">—</p>
            )}
          </div>
          <div className="stat-tile">
            <p className="stat-tile__label">Sync events (30d)</p>
            <p className="stat-tile__value">{tenant.usage.sync_events_last_30d.toLocaleString()}</p>
          </div>
        </div>
      </div>

      <div className="panel">
        <h2 className="panel__title">
          Onboarding checklist{" "}
          <MockBadge
            live={isRealMode}
            label={isRealMode ? "live" : "mock"}
            title={
              isRealMode
                ? "Computed live from this business's real branch/product/sale counts, not a stored checklist."
                : "Synthesized from this mock's own fixture checklist state, in the same real shape."
            }
          />
        </h2>
        {checklistError ? (
          <StatusBanner kind="error">{checklistError}</StatusBanner>
        ) : checklistLoading || !checklist ? (
          <p className="muted">Loading…</p>
        ) : (
          <ul className="checklist">
            {checklist.steps.map((step) => (
              <li key={step.step} className={`checklist__item ${step.completed ? "checklist__item--done" : ""}`}>
                <span className="checklist__check">{step.completed ? "✓" : ""}</span>
                <span style={{ flex: 1 }}>{step.label}</span>
                {step.completed_at ? (
                  <span className="muted" style={{ fontSize: "0.75rem" }}>
                    {new Date(step.completed_at).toLocaleDateString()}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        <p className="muted" style={{ marginTop: 10, fontSize: "0.78rem" }}>
          Computed live from this business's actual data — not something an operator can toggle directly.
        </p>
      </div>

      <ConfirmDialog
        open={suspendDialog.open}
        title={`Suspend ${tenant.business_name}?`}
        body="This flips a flag checked at the auth/API layer — it never deletes or modifies the business's data. Logins for this business's users will be rejected until reactivated."
        confirmLabel="Suspend"
        danger
        pending={suspendDialog.pending}
        onCancel={() => {
          suspendDialog.setOpen(false);
          setSuspendReason("");
        }}
        onConfirm={() =>
          suspendDialog.run(
            () => client.suspendTenant(tenant.id, suspendReason || "No reason given"),
            () => {
              setSuspendReason("");
              reload();
            },
          )
        }
      />
      {suspendDialog.open ? (
        <div className="panel" style={{ maxWidth: 360 }}>
          <label className="sl-field__label" htmlFor="suspend-reason">
            Reason (recorded in the audit log)
          </label>
          <input
            id="suspend-reason"
            className="sl-input"
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            placeholder="e.g. non-payment"
          />
        </div>
      ) : null}

      <ConfirmDialog
        open={reactivateDialog.open}
        title={`Reactivate ${tenant.business_name}?`}
        body="This clears the suspension flag — the business's users will be able to log in again immediately."
        confirmLabel="Reactivate"
        pending={reactivateDialog.pending}
        onCancel={() => reactivateDialog.setOpen(false)}
        onConfirm={() => reactivateDialog.run(() => client.reactivateTenant(tenant.id), () => reload())}
      />

      <ConfirmDialog
        open={backupDialog.open}
        title={`Trigger an on-demand backup for ${tenant.business_name}?`}
        body="Records that a backup was requested. No real backup infrastructure exists yet — this logs the request but doesn't queue a real backup job."
        confirmLabel="Trigger backup"
        pending={backupDialog.pending}
        onCancel={() => backupDialog.setOpen(false)}
        onConfirm={() =>
          backupDialog.run(
            () => client.triggerBackup(tenant.id),
            (result) =>
              setBackupResult(
                result.status === "ACKNOWLEDGED"
                  ? `Backup request ${result.backup_id} acknowledged and audit-logged — no real backup job was queued (stub).`
                  : `Backup ${result.backup_id} queued (mocked).`,
              ),
          )
        }
      />

      <ConfirmDialog
        open={resetDemoDialog.open}
        title={`Reset ${tenant.business_name}'s data now?`}
        body="Wipes and reseeds this tenant's catalog, stock, sales, and customer data back to the default demo state — the same reset that runs automatically every 20 minutes when the demo has been changed. Login credentials are never touched."
        confirmLabel="Reset demo data"
        danger
        pending={resetDemoDialog.pending}
        onCancel={() => resetDemoDialog.setOpen(false)}
        onConfirm={() =>
          resetDemoDialog.run(
            () => client.resetDemoData(tenant.id),
            (result) => setResetDemoResult(`Demo data reset at ${new Date(result.reset_at).toLocaleString()}.`),
          )
        }
      />

      <ConfirmDialog
        open={impersonateDialog.open}
        title={`View ${tenant.business_name} as its OWNER?`}
        body="Issues a real, short-lived (15 minute) session for support use. This is a genuine, fully-authorization-capable login as this business's user — it is logged the instant it's issued, and a persistent banner will show on every screen for as long as the session is active. Opens a new tab, already logged in as this business, ready to do support work."
        confirmLabel="Start viewing as business"
        pending={impersonateDialog.pending}
        onCancel={() => {
          impersonateDialog.setOpen(false);
          setImpersonateReason("");
        }}
        onConfirm={() =>
          impersonateDialog.run(
            () => client.impersonateTenant(tenant.id, undefined, impersonateReason || undefined),
            (session) => {
              setImpersonateReason("");
              impersonation.start(session, tenant.business_name);
              // Auto-open a REAL, already-logged-in dashboard-admin tab so the operator
              // can go straight to doing support work there (§9.1) — in mock mode the
              // handoff code was never issued by a real backend and would just fail
              // confusingly on landing, so this stays platform-admin-only there, same
              // rule `ImpersonationViewPage` already applies to its own live API calls.
              if (isRealMode) {
                window.open(
                  `${DASHBOARD_URL}/impersonate?code=${encodeURIComponent(session.dashboard_handoff_code)}`,
                  "_blank",
                  "noopener,noreferrer",
                );
              }
              navigate(`/tenants/${tenant.id}/impersonate`);
            },
          )
        }
      />
      {impersonateDialog.open ? (
        <div className="panel" style={{ maxWidth: 360 }}>
          <label className="sl-field__label" htmlFor="impersonate-reason">
            Reason (recorded in the audit log)
          </label>
          <input
            id="impersonate-reason"
            className="sl-input"
            value={impersonateReason}
            onChange={(e) => setImpersonateReason(e.target.value)}
            placeholder="e.g. support ticket #42"
          />
        </div>
      ) : null}
    </div>
  );
}
